import React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmailPreviewFrameProps {
  subject: string;
  html: string;
  loading?: boolean;
  error?: string | null;
  /** Sizes the preview; the email scrolls inside its card. */
  className?: string;
  /** A control at the right of the subject bar (e.g. open full size). */
  action?: React.ReactNode;
  "data-testid"?: string;
}

/**
 * A server-rendered email under its subject line. The HTML can be
 * admin-authored, so it is shown in a sandboxed iframe that can't run scripts
 * or reach the app.
 */
const EmailPreviewFrame: React.FC<EmailPreviewFrameProps> = ({
  subject,
  html,
  loading = false,
  error,
  className,
  action,
  "data-testid": testId,
}) => (
  <div
    className={cn(
      "flex min-w-0 flex-col overflow-hidden rounded-md border bg-background",
      className
    )}
    data-testid={testId}
  >
    <div className="flex items-center justify-between gap-2 border-b px-3 py-2 text-sm">
      <span className="min-w-0 truncate">
        <span className="text-muted-foreground">Subject: </span>
        <span className="font-medium">{subject || "…"}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1">
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        {action}
      </span>
    </div>
    {error ? (
      <p className="p-3 text-xs text-destructive">{error}</p>
    ) : (
      <iframe
        title="Email preview"
        sandbox=""
        srcDoc={html}
        className="min-h-0 w-full flex-1 bg-[#f3f4f6]"
      />
    )}
  </div>
);

export default EmailPreviewFrame;
